/**
 * VendorSetupPassword.jsx — magic-link landing at /vendor/setup/:token.
 * Vendors arrive here from the approval email, pick a password, and get logged
 * straight into /vendor.  No founder-app onboarding flow involved.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Lock, Spinner, ArrowRight, Buildings, Bank, Warning, Check } from '@phosphor-icons/react';

const VendorSetupPassword = ({ onLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/vendor/setup/${token}`);
        if (!cancelled) setInfo(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Invalid setup link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (!fullName.trim()) { toast.error('Please enter your name'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/vendor/setup/${token}`, {
        full_name: fullName.trim(),
        password,
      });
      const user = r.data.user || {};
      localStorage.setItem('token', r.data.token);
      localStorage.setItem('user', JSON.stringify(user));
      onLogin?.(user, r.data.token);
      toast.success(`Welcome, ${(user.full_name || '').split(' ')[0]}!`);
      navigate('/vendor', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const isService = info?.vendor_type === 'service';
  const accent = isService ? '#10b981' : '#f59e0b';
  const Icon = isService ? Buildings : Bank;
  const typeLabel = isService ? 'Startup Services' : 'Startup Credit';
  const loginPath = isService ? '/services/login' : '/credit/login';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-muted text-sm inline-flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Verifying link…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="vendor-setup-error">
        <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full text-center">
          <Warning size={28} className="mx-auto mb-3 text-warning" />
          <h2 className="text-xl font-heading font-bold mb-2">This link can&rsquo;t be used</h2>
          <p className="text-sm text-text-secondary mb-4">{error}</p>
          <Link to="/vendors" className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg bg-text-primary text-background">Back to Vendor Center</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-text-primary flex items-center justify-center p-4" data-testid="vendor-setup-page">
      <div
        aria-hidden
        className="fixed inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: `linear-gradient(${accent}11 1px, transparent 1px), linear-gradient(90deg, ${accent}11 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-2.5 mb-6">
          <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
            <Icon size={20} weight="fill" color="white" />
          </span>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: accent }}>Founderboard · Vendor</div>
            <div className="text-base font-heading font-bold leading-none" style={{ letterSpacing: '-0.01em' }}>{typeLabel}</div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-2xl p-6 mb-4 flex items-start gap-3" style={{ borderLeft: `4px solid ${accent}` }}>
          <Check size={18} weight="bold" style={{ color: accent }} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-0.5" style={{ color: accent }}>Approved</div>
            <p className="text-sm text-text-secondary leading-relaxed"><strong className="text-text-primary">{info.vendor_name}</strong> is approved. Set a password to access your dashboard and publish offerings.</p>
          </div>
        </div>

        <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2" style={{ letterSpacing: '-0.02em' }}>
          Welcome to <span style={{ color: accent }}>Founderboard</span>
        </h1>
        <p className="text-sm text-text-muted mb-6">Signing in as <strong className="text-text-primary">{info.contact_email}</strong></p>

        <form onSubmit={submit} className="space-y-4" data-testid="vendor-setup-form">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">Your name</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Lola Singh" data-testid="vendor-setup-name" required className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-text-primary placeholder:text-text-faint" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">Choose a password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="vendor-setup-password" autoComplete="new-password" required minLength={8} className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-text-primary placeholder:text-text-faint" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">Confirm password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" data-testid="vendor-setup-confirm" autoComplete="new-password" required minLength={8} className="w-full bg-surface border border-border rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-text-primary placeholder:text-text-faint" />
          </div>
          <button type="submit" disabled={busy} data-testid="vendor-setup-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90" style={{ backgroundColor: accent }}>
            {busy ? <Spinner size={14} className="animate-spin" /> : <Lock size={14} weight="bold" />}
            Set password &amp; sign in
            {!busy && <ArrowRight size={14} weight="bold" />}
          </button>
        </form>

        <div className="mt-6 text-[11px] text-text-dim text-center">
          Already finished setup? <Link to={loginPath} className="underline hover:text-text-primary">Go to login</Link>
        </div>
      </div>
    </div>
  );
};

export default VendorSetupPassword;
